import { useState, FormEvent } from 'react'
import {
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js'
import { ShoppingCart } from 'lucide-react'
import LoadingSpinner from './LoadingSpinner'

interface StripePaymentFormProps {
  credits: number
  price: number
  onSuccess: () => void
  onError: (error: string) => void
  clientSecret: string
}

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
}

export default function StripePaymentForm({
  credits,
  price,
  onSuccess,
  onError,
  clientSecret
}: StripePaymentFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [processing, setProcessing] = useState(false)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()

    if (!stripe || !elements) {
      onError('Stripe 未正确加载')
      return
    }

    const cardElement = elements.getElement(CardElement)
    if (!cardElement) {
      onError('支付表单未正确加载')
      return
    }

    setProcessing(true)

    try {
      // Confirm the payment
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            // 可以添加账单信息
          },
        }
      })

      if (error) {
        onError(error.message || '支付失败')
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        onSuccess()
      } else {
        onError('支付未完成')
      }
    } catch (err) {
      onError('支付处理失败')
    } finally {
      setProcessing(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          支付信息
        </label>
        <div className="p-4 border border-gray-300 rounded-lg bg-white">
          <CardElement options={cardElementOptions} />
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex justify-between items-center text-sm">
          <span>购买积分：</span>
          <span className="font-medium">{credits} 积分</span>
        </div>
        <div className="flex justify-between items-center text-sm mt-1">
          <span>支付金额：</span>
          <span className="font-medium">¥{price}</span>
        </div>
      </div>

      <button
        type="submit"
        disabled={!stripe || processing}
        className="w-full flex justify-center items-center space-x-2 btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {processing ? (
          <LoadingSpinner size="sm" />
        ) : (
          <>
            <ShoppingCart className="w-4 h-4" />
            <span>确认支付 ¥{price}</span>
          </>
        )}
      </button>

      <p className="text-xs text-gray-500 text-center">
        支付由 Stripe 安全处理，我们不会存储您的支付信息
      </p>
    </form>
  )
}